import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';

@Component({
  standalone: true,
  selector: 'crm-user-multi-select',
  templateUrl: './user-multi-select.component.html',
  imports: [CommonModule, MultiSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserMultiSelectComponent implements OnInit, OnDestroy {
  @ViewChild('multiSelectComponent') public multiSelectComponent: MultiSelectComponent;

  @Input() public users: UserFindInterface[] = [];

  @Input() public selectedUsers: UserFindInterface[] = [];
  @Output() public selectedUsersChange = new EventEmitter<UserFindInterface[]>();

  @Output() public openedChange = new EventEmitter<boolean>();

  @Input() public withBots = false;
  @Input() public includeIds: number[] = [];

  @Input() public customRequest = false;
  @Input() public class = '';
  @Input() public styleClass: string;
  @Input() public appendTo: string;
  @Input() public scrollHeight: string = '300px';
  @Input() public disabled = false;
  @Input() public itemSubDescriptionField = 'job';
  @Input() public showClear = true;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public moveDown = false;
  @Input() public sort = 'profile.name:string';
  @Input() public label = 'Пользователь';

  private loadedUsers = false;
  private timer: NodeJS.Timer;

  @Input() public set setSelectedUserIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }

    if (
      ids?.length &&
      ((this.selectedUsers?.length && !this.selectedUsers?.every((selectedUser) => ids.includes(selectedUser.id))) ||
        !this.selectedUsers?.length)
    ) {
      if (this.loadedUsers) {
        this.selectedUsers = this.users.filter((user) => ids.includes(user.id));
      } else {
        if (this.timer) {
          clearInterval(this.timer);
        }
        this.timer = setInterval(() => {
          if (this.loadedUsers) {
            this.selectedUsers = this.users.filter((user) => ids.includes(user.id));
            this.cdRef.markForCheck();
            clearInterval(this.timer);
          }
        }, 500);
      }
    }
  }

  public constructor(
    private readonly userService: UserService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService
  ) {}

  public ngOnInit(): void {
    if (
      !this.customRequest &&
      (this.globalService.globalInfo.tenantInfo?.ratePlan?.availableModules || []).includes('BASIS')
    ) {
      this.userService.findAll({ withBots: this.withBots, includeIds: this.includeIds }).subscribe({
        next: (users) => {
          this.users = users;
          if (
            this.users?.length &&
            this.selectedUsers?.length &&
            !this.selectedUsers.every((selectedUser) => this.users.some((user) => selectedUser.id === user.id))
          ) {
            const deletedUsers = this.selectedUsers.filter(
              (selectedUser) => !this.users.some((user) => selectedUser.id === user.id)
            );
            this.userService
              .findAll({
                withBots: this.withBots,
                excludeIds: this.users.map((user) => user.id),
                includeIds: deletedUsers.map((user) => user.id),
                withBanned: true
              })
              .subscribe({
                next: (newUsers) => {
                  this.users = [...this.users, ...newUsers];
                  this.cdRef.markForCheck();
                }
              });
          }
          this.loadedUsers = true;
          this.cdRef.markForCheck();
        },
        error: () => {
          if (this.timer) {
            clearInterval(this.timer);
          }
        }
      });
    } else {
      if (this.selectedUsers?.filter((selectedUser) => selectedUser?.banned)?.length) {
        this.users = this.users.concat(this.selectedUsers.filter((selectedUser) => selectedUser?.banned));
        this.cdRef.markForCheck();
      }
      this.loadedUsers = true;
    }
  }

  public ngOnDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  public toUser(user: any): UserFindInterface {
    return user as UserFindInterface;
  }
}
